import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// types.
import { ComputeServiceType, Project, Site, UserType } from '../utils/types';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

export interface GlobalState {
  loggedIn: boolean | undefined;
  sessionValid: boolean | undefined;
  loggedInRefreshTrigger: number | undefined;
  username: string | undefined;
  preferredUsername: string | undefined;
  siteCapabilitiesTokenObtained: boolean | undefined;
  dataManagementTokenObtained: boolean | undefined;
  gatewayBackendToken: string | undefined;
  narrowScreen: boolean;
  site: Site | undefined;
  computeServicesLoaded: boolean;
  selectedCanfarId: string | undefined;
  project: Project | undefined;
  darkMode: boolean;
  loadedNotifications: boolean;
  topMenuPosition: {
    leftUpper: number;
    widthUpper: number;
    leftLower: number;
    widthLower: number;
    offsetLeftLower: number;
  };
  submittingJob: boolean;
  userType: UserType | undefined;
}

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

const initialState: GlobalState = {
  loggedIn: JSON.parse(sessionStorage.getItem('appState') ?? '{}').loggedIn || false,
  sessionValid: JSON.parse(sessionStorage.getItem('appState') ?? '{}').sessionValid,
  loggedInRefreshTrigger: JSON.parse(sessionStorage.getItem('appState') ?? '{}')
    .loggedInRefreshTrigger,
  username: JSON.parse(sessionStorage.getItem('appState') ?? '{}').username,
  preferredUsername: JSON.parse(sessionStorage.getItem('appState') ?? '{}')
    .preferredUsername,
  siteCapabilitiesTokenObtained: JSON.parse(sessionStorage.getItem('appState') ?? '{}')
    .siteCapabilitiesTokenObtained,
  dataManagementTokenObtained: JSON.parse(sessionStorage.getItem('appState') ?? '{}')
    .dataManagementTokenObtained,
  gatewayBackendToken: undefined,
  narrowScreen: false,
  site: undefined,
  computeServicesLoaded: false,
  selectedCanfarId: undefined,
  project: undefined,
  darkMode: JSON.parse(sessionStorage.getItem('appState') ?? '{}').darkMode,
  loadedNotifications: false,
  topMenuPosition: {
    leftUpper: 0,
    widthUpper: 0,
    leftLower: 0,
    widthLower: 0,
    offsetLeftLower: 0,
  },
  submittingJob: false,
  userType:
    JSON.parse(sessionStorage.getItem('appState') ?? '{}').userType === 2
      ? UserType.DEVELOPER
      : JSON.parse(sessionStorage.getItem('appState') ?? '{}').userType === 1
        ? UserType.OPERATOR
        : UserType.STANDARD,
};

//	--------------------------------------------------------------------------
//
//	S L I C E S
//
//	--------------------------------------------------------------------------

export const globalSlice = createSlice({
  name: 'global',
  initialState: initialState,
  reducers: {
    //
    rG_setComputeServicesLoaded: (state: GlobalState, action: PayloadAction<boolean>) => {
      state.computeServicesLoaded = action.payload;
    },

    rG_setDarkMode: (state: GlobalState, action: PayloadAction<boolean>) => {
      state.darkMode = action.payload;
    },

    rG_setDataManagementTokenObtained: (
      state: GlobalState,
      action: PayloadAction<boolean | undefined>,
    ) => {
      state.dataManagementTokenObtained = action.payload;
    },

    rG_setGatewayBackendToken: (
      state: GlobalState,
      action: PayloadAction<string | undefined>,
    ) => {
      state.gatewayBackendToken = action.payload;
    },

    rG_setLoadedNotifications: (state: GlobalState, action: PayloadAction<boolean>) => {
      state.loadedNotifications = action.payload;
    },

    rG_setLoggedIn: (state: GlobalState, action: PayloadAction<boolean | undefined>) => {
      state.loggedIn = action.payload;
    },

    rG_setLoggedInRefreshTrigger: (
      state: GlobalState,
      action: PayloadAction<number | undefined>,
    ) => {
      state.loggedInRefreshTrigger = action.payload;
    },

    rG_setNarrowScreen: (state: GlobalState, action: PayloadAction<boolean>) => {
      state.narrowScreen = action.payload;
    },

    rG_setPreferredUsername: (
      state: GlobalState,
      action: PayloadAction<string | undefined>,
    ) => {
      state.preferredUsername = action.payload;
    },

    rG_setProject: (state: GlobalState, action: PayloadAction<Project | undefined>) => {
      state.project = action.payload;
      if (action.payload !== undefined) {
        sessionStorage.setItem('project_id', JSON.stringify({ id: action.payload.id }));
      } else {
        sessionStorage.remove('project_id');
      }
    },

    rG_setSelectedCanfarId: (
      state: GlobalState,
      action: PayloadAction<string | undefined>,
    ) => {
      state.selectedCanfarId = action.payload;
    },

    rG_setSessionValid: (
      state: GlobalState,
      action: PayloadAction<boolean | undefined>,
    ) => {
      state.sessionValid = action.payload;
    },

    rG_setSite: (state: GlobalState, action: PayloadAction<Site>) => {
      state.site = action.payload;
      if (action.payload.site_uuid !== undefined) {
        sessionStorage.setItem('site_uuid', action.payload.site_uuid);
      } else {
        sessionStorage.removeItem('site_uuid');
      }
    },

    rG_setSiteCapabilitiesTokenObtained: (
      state: GlobalState,
      action: PayloadAction<boolean | undefined>,
    ) => {
      state.siteCapabilitiesTokenObtained = action.payload;
    },

    rG_setSubmittingJob: (state: GlobalState, action: PayloadAction<boolean>) => {
      state.submittingJob = action.payload;
    },

    rG_setTopMenuPosition: (
      state: GlobalState,
      action: PayloadAction<{
        leftUpper?: number;
        widthUpper?: number;
        widthLower?: number;
        offsetLeftLower?: number;
      }>,
    ) => {
      //
      if (action.payload.leftUpper !== undefined) {
        state.topMenuPosition.leftUpper = action.payload.leftUpper;
      }
      if (action.payload.widthUpper !== undefined) {
        state.topMenuPosition.widthUpper = action.payload.widthUpper;
      }
      if (action.payload.widthLower !== undefined) {
        state.topMenuPosition.widthLower = action.payload.widthLower;
      }
      if (action.payload.offsetLeftLower !== undefined) {
        state.topMenuPosition.offsetLeftLower = action.payload.offsetLeftLower;
      }
      state.topMenuPosition.leftLower =
        state.topMenuPosition.leftUpper -
        state.topMenuPosition.offsetLeftLower +
        (state.topMenuPosition.widthUpper - state.topMenuPosition.widthLower) / 2;
      //
    },

    rG_setUsername: (state: GlobalState, action: PayloadAction<string | undefined>) => {
      state.username = action.payload;
    },

    rG_setUserType: (state: GlobalState, action: PayloadAction<UserType | undefined>) => {
      state.userType = action.payload;
    },
    //
  },
});

// expose the interface.
export const {
  rG_setLoggedIn,
  rG_setSessionValid,
  rG_setLoggedInRefreshTrigger,
  rG_setSiteCapabilitiesTokenObtained,
  rG_setDataManagementTokenObtained,
  rG_setGatewayBackendToken,
  rG_setSite,
  rG_setComputeServicesLoaded,
  rG_setNarrowScreen,
  rG_setSelectedCanfarId,
  rG_setProject,
  rG_setUsername,
  rG_setPreferredUsername,
  rG_setDarkMode,
  rG_setSubmittingJob,
  rG_setTopMenuPosition,
  rG_setUserType,
} = globalSlice.actions;

export default globalSlice.reducer;
